import { Button } from '@ipfabric/ui-components/Button/Button';
import { FormField } from '@ipfabric/ui-components/FormField/FormField';
import { Icon } from '@ipfabric/ui-components/Icon/Icon';
import { TableButton } from '@ipfabric/ui-components/TableButton/TableButton';
import classNames from 'classnames';
import { FormInputButton } from 'components/FormInputButton.tsx/FormInputButton';
import { type ChangeEvent, type KeyboardEvent, useState } from 'react';
import {
  mergeLabelValues,
  parseLabelValueInput,
  replaceLabelValue,
} from './labelValueNormalization';
import { getLabelValueValidationError } from './labelValueValidation';

const VALUE_ROW_ACTIONS_CLASS_NAME = 'flex w-[56px] shrink-0 items-stretch';
const VALUE_ROW_ACTION_SLOT_CLASS_NAME =
  'flex min-w-0 flex-1 items-center justify-center';

const getLabelValueInputContainerClassName = ({
  disabled,
  error,
  variant,
}: {
  disabled: boolean;
  error?: string;
  variant: 'add' | 'edit';
}) =>
  classNames(
    'flex min-w-0 flex-1 border border-default bg-default focus-within:border-status-selected',
    variant === 'add'
      ? 'h-[32px] items-center rounded-l rounded-r-none border-r-0 px-2xs focus-within:z-[1]'
      : 'items-stretch overflow-hidden rounded-sm',
    disabled && 'cursor-default bg-status-disabled text-status-disabled-dark',
    error && 'border-status-error',
  );

type LabelValuesEditorProps = {
  disabled?: boolean;
  error?: string;
  /** Label the values belong to; enables reserved value checks (ipf_region). */
  labelName?: string;
  onChange: (values: string[]) => void;
  values: string[];
};

export const LabelValuesEditor = ({
  disabled = false,
  error,
  labelName,
  onChange,
  values,
}: LabelValuesEditorProps) => {
  const [inputValue, setInputValue] = useState('');
  const [editedIndex, setEditedIndex] = useState<number | null>(null);
  const [editedValue, setEditedValue] = useState('');
  // The error is tied to the label name it was computed for: reserved value
  // rules (ipf_region) stop applying once the name changes.
  const [localError, setLocalErrorState] = useState<
    { message: string; labelName: string | undefined } | undefined
  >();
  const setLocalError = (message: string | undefined) =>
    setLocalErrorState(message ? { message, labelName } : undefined);
  const displayError =
    localError && localError.labelName === labelName
      ? localError.message
      : error;

  const addValues = () => {
    const parsedValues = parseLabelValueInput(inputValue);
    if (parsedValues.length === 0) {
      return;
    }

    for (const value of parsedValues) {
      const validationError = getLabelValueValidationError(value, labelName);
      if (validationError) {
        setLocalError(validationError);
        return;
      }
    }

    setLocalError(undefined);
    onChange(mergeLabelValues(values, parsedValues));
    setInputValue('');
  };

  const handleAddInputKeyDown = (event: KeyboardEvent<HTMLInputElement>) => {
    if (event.key === 'Enter' || event.key === 'Tab') {
      addValues();

      if (event.key === 'Enter') {
        event.preventDefault();
        event.stopPropagation();
      }
    }
  };

  const saveEditedValue = (index: number) => {
    const validationError = getLabelValueValidationError(
      editedValue,
      labelName,
    );
    if (validationError) {
      setLocalError(validationError);
      return;
    }

    setLocalError(undefined);
    onChange(replaceLabelValue(values, index, editedValue));
    setEditedIndex(null);
    setEditedValue('');
  };

  const cancelEditedValue = () => {
    setEditedIndex(null);
    setEditedValue('');
  };

  const editSuffixButtonClassName =
    'flex h-full w-full items-center justify-center hover:bg-state-hover active:bg-state-press disabled:pointer-events-none disabled:text-status-disabled';

  const handleEditedValueKeyDown = (
    event: KeyboardEvent<HTMLInputElement>,
    index: number,
  ) => {
    if (event.key === 'Enter') {
      event.preventDefault();
      saveEditedValue(index);
    }

    if (event.key === 'Escape') {
      event.preventDefault();
      cancelEditedValue();
    }
  };

  return (
    <FormField
      label='Possible values'
      description='Type in one or more values and click + Add button.'
      error={displayError}
    >
      <div className='flex flex-col gap-xs'>
        <div className='flex w-full items-stretch'>
          <div
            className={getLabelValueInputContainerClassName({
              disabled,
              error: displayError,
              variant: 'add',
            })}
          >
            <input
              className='min-w-0 flex-1 border-0 bg-transparent text-sm text-default outline-none placeholder:text-plain disabled:cursor-default disabled:text-status-disabled-dark'
              data-testid='label-values-input'
              disabled={disabled}
              onChange={(event: ChangeEvent<HTMLInputElement>) =>
                setInputValue(event.target.value)
              }
              onKeyDown={handleAddInputKeyDown}
              placeholder='Insert one or more values'
              type='text'
              value={inputValue}
            />
          </div>
          <Button
            className='h-[32px] shrink-0 rounded-none rounded-r !rounded-l-none'
            dataTestId='label-values-add'
            disabled={disabled}
            icon='fa-plus-circle'
            onClick={() => {
              addValues();
            }}
            size='small'
            type='button'
            variant='outline'
          >
            Add
          </Button>
        </div>
        {values.length > 0 && (
          <div className='flex flex-col gap-2xs'>
            {values.map((value, index) => (
              <div
                className='flex min-h-[32px] w-full items-stretch'
                key={value}
              >
                {editedIndex === index ? (
                  <div
                    className={getLabelValueInputContainerClassName({
                      disabled,
                      error: displayError,
                      variant: 'edit',
                    })}
                  >
                    <div className='flex min-w-0 flex-1 items-center gap-3xs px-2xs py-2xs'>
                      <Icon
                        glyph='fa-pen-to-square'
                        iconStyle='regular'
                        size='xs'
                      />
                      <input
                        autoFocus
                        className='min-w-0 flex-1 border-0 bg-transparent text-xs text-default outline-none disabled:cursor-default disabled:text-status-disabled-dark'
                        data-testid='label-value-edit-input'
                        disabled={disabled}
                        onChange={(event: ChangeEvent<HTMLInputElement>) =>
                          setEditedValue(event.target.value)
                        }
                        onKeyDown={(event) =>
                          handleEditedValueKeyDown(event, index)
                        }
                        type='text'
                        value={editedValue}
                      />
                    </div>
                    <div className={VALUE_ROW_ACTIONS_CLASS_NAME}>
                      <div
                        className={classNames(
                          VALUE_ROW_ACTION_SLOT_CLASS_NAME,
                          'border-l border-l-default',
                        )}
                      >
                        <FormInputButton
                          className={editSuffixButtonClassName}
                          dataTestId={`label-value-${index}-save`}
                          disabled={disabled}
                          onClick={() => saveEditedValue(index)}
                        >
                          <Icon
                            glyph='fa-floppy-disk'
                            iconStyle='regular'
                            size='xs'
                          />
                        </FormInputButton>
                      </div>
                      <div
                        className={classNames(
                          VALUE_ROW_ACTION_SLOT_CLASS_NAME,
                          'border-l border-l-default',
                        )}
                      >
                        <FormInputButton
                          className={editSuffixButtonClassName}
                          dataTestId={`label-value-${index}-cancel`}
                          disabled={disabled}
                          onClick={cancelEditedValue}
                        >
                          <Icon
                            glyph='fa-times'
                            iconStyle='regular'
                            size='xs'
                          />
                        </FormInputButton>
                      </div>
                    </div>
                  </div>
                ) : (
                  <>
                    <span className='min-w-0 flex-1 self-center truncate'>
                      {value}
                    </span>
                    <div className={VALUE_ROW_ACTIONS_CLASS_NAME}>
                      <div className={VALUE_ROW_ACTION_SLOT_CLASS_NAME}>
                        <TableButton
                          dataTestId={`label-value-${index}-edit`}
                          disabled={disabled}
                          icon='fa-pen'
                          onClick={() => {
                            setEditedIndex(index);
                            setEditedValue(value);
                          }}
                          type='button'
                          variant='plain'
                        />
                      </div>
                      <div className={VALUE_ROW_ACTION_SLOT_CLASS_NAME}>
                        <TableButton
                          accent='negative'
                          dataTestId={`label-value-${index}-remove`}
                          disabled={disabled}
                          icon='fa-trash-can'
                          onClick={() =>
                            onChange(
                              values.filter(
                                (_, valueIndex) => valueIndex !== index,
                              ),
                            )
                          }
                          type='button'
                          variant='plain'
                        />
                      </div>
                    </div>
                  </>
                )}
              </div>
            ))}
          </div>
        )}
      </div>
    </FormField>
  );
};
